import type { IChatFolderListObject } from "@frontend-shared/types";
import { useDirection } from "@radix-ui/react-direction";
import {
  Archive,
  ArchiveRestore,
  Download,
  FolderInput,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Share2,
  Trash,
} from "lucide-react";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useChatFolderList } from "@/api/chat-folder/useChatFolderList";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuIcon,
  DropdownMenuIconItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface IChatActionMenuProps {
  onRenameClick: () => void;
  onShareClick: () => void;
  onDownloadFilesClick: () => void;
  onArchiveClick: () => void;
  onDeleteClick: () => void;
  onPinClick: () => void;
  onMoveToFolder: (folderUuid: string | null) => void;
  onCreateFolderAndMove: () => void;
  isMenuOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isArchived: boolean;
  isPinned: boolean;
  currentFolderId?: string | null;
  hasChatShareWritePermission: boolean;
  hasActiveShare: boolean;
}

const ChatActionMenuInner: React.FC<IChatActionMenuProps> = ({
  onRenameClick,
  onShareClick,
  onDownloadFilesClick,
  onArchiveClick,
  onDeleteClick,
  onPinClick,
  onMoveToFolder,
  onCreateFolderAndMove,
  isMenuOpen,
  onOpenChange,
  isArchived,
  isPinned,
  currentFolderId,
  hasChatShareWritePermission,
  hasActiveShare,
}) => {
  const { t } = useTranslation("sidebar");
  const dir = useDirection();
  const [isMoveSubmenuOpen, setIsMoveSubmenuOpen] = useState(false);
  const {
    data: folders = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isFoldersLoading,
  } = useChatFolderList();

  const availableFolders = useMemo(
    (): IChatFolderListObject[] =>
      folders.filter((folder) => folder.uuid !== currentFolderId),
    [folders, currentFolderId],
  );

  const handleFolderListScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>): void => {
      if (!hasNextPage || isFetchingNextPage || !isMoveSubmenuOpen) {
        return;
      }

      const viewportElement = event.currentTarget;
      const isNearBottom =
        viewportElement.scrollTop + viewportElement.clientHeight >=
        viewportElement.scrollHeight - 24;

      if (isNearBottom) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, isMoveSubmenuOpen],
  );

  const handleMenuClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRenameClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onRenameClick();
  };

  const handleShareClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onShareClick();
  };

  const handleDownloadFilesClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onDownloadFilesClick();
  };

  const handleArchiveClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onArchiveClick();
  };

  const handleDeleteClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onDeleteClick();
  };

  const handlePinClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onPinClick();
  };

  return (
    <DropdownMenu dir={dir} open={isMenuOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild onClick={handleMenuClick}>
        <Button
          variant="ghost"
          size="icon"
          data-testid="chat-action-menu-trigger"
          className={cn(
            // NOTE: NSAI-6259 — `text-black` was hardcoded here, so in dark theme the
            // trigger was a black glyph on a dark row: invisible until the cursor
            // reached it and the button's own hover colour took over. It follows the
            // sidebar's foreground tokens now, which are light in dark theme and dark
            // in light theme, so it reads the same way in both.
            "absolute top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-150 group-hover/menu-item:opacity-100 ltr:right-1 rtl:left-1 h-6 w-7 shrink-0 z-10 text-sidebar-foreground-secondary hover:text-sidebar-foreground bg-transparent hover:bg-transparent",
            isMenuOpen && "opacity-100",
          )}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-0">
        {isArchived ? (
          <DropdownMenuIconItem
            onClick={handleArchiveClick}
            data-testid="chat-action-unarchive"
            icon={<ArchiveRestore className="h-4 w-4" />}
          >
            <span>{t("chat_list.unarchive")}</span>
          </DropdownMenuIconItem>
        ) : (
          <>
            <DropdownMenuIconItem
              onClick={handleRenameClick}
              data-testid="chat-action-rename"
              icon={<Pencil className="h-4 w-4" />}
            >
              <span>{t("chat_list.rename")}</span>
            </DropdownMenuIconItem>
            {hasChatShareWritePermission && (
              <>
                <DropdownMenuSeparator className="my-0" />
                <DropdownMenuIconItem
                  onClick={handleShareClick}
                  data-testid="chat-action-share"
                  icon={<Share2 className="h-4 w-4" />}
                >
                  <span>
                    {hasActiveShare
                      ? t("chat_list.shared")
                      : t("chat_list.share")}
                  </span>
                </DropdownMenuIconItem>
              </>
            )}
            <DropdownMenuSeparator className="my-0" />
            {/* NOTE: NSAI-5312 — opens a dialog listing the chat's generated
                files, read from the server so none are missed by the history
                only being partly loaded. */}
            <DropdownMenuIconItem
              onClick={handleDownloadFilesClick}
              data-testid="chat-action-download-files"
              icon={<Download className="h-4 w-4" />}
            >
              <span>{t("chat_list.download_files")}</span>
            </DropdownMenuIconItem>
            <DropdownMenuSeparator className="my-0" />
            <DropdownMenuIconItem
              onClick={handlePinClick}
              data-testid={isPinned ? "chat-action-unpin" : "chat-action-pin"}
              icon={
                isPinned ? (
                  <PinOff className="h-4 w-4" />
                ) : (
                  <Pin className="h-4 w-4" />
                )
              }
            >
              <span>
                {isPinned
                  ? t("chat_folder_list.unpin")
                  : t("chat_folder_list.pin")}
              </span>
            </DropdownMenuIconItem>
            <DropdownMenuSeparator className="my-0" />
            <DropdownMenuSub
              open={isMoveSubmenuOpen}
              onOpenChange={setIsMoveSubmenuOpen}
            >
              <DropdownMenuSubTrigger
                data-testid="chat-action-move-to-folder"
                className="gap-2 rounded-none group"
              >
                <DropdownMenuIcon className="group-data-[state=open]:bg-dropdown-menu-icon-background-hover">
                  <FolderInput className="h-4 w-4" />
                </DropdownMenuIcon>
                <span>{t("chat_folder_list.move_to_folder")}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-0 min-w-[16rem]">
                {currentFolderId && (
                  <>
                    <DropdownMenuIconItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onMoveToFolder(null);
                      }}
                      icon={<FolderInput className="h-4 w-4" />}
                    >
                      <span>{t("chat_folder_list.remove_from_folder")}</span>
                    </DropdownMenuIconItem>
                    <DropdownMenuSeparator className="my-0" />
                  </>
                )}
                <DropdownMenuIconItem
                  onClick={(event) => {
                    event.stopPropagation();
                    onCreateFolderAndMove();
                  }}
                  icon={<FolderPlus className="h-4 w-4" />}
                >
                  <span>{t("chat_folder_list.new_folder")}</span>
                </DropdownMenuIconItem>
                {(isFoldersLoading ||
                  availableFolders.length > 0 ||
                  hasNextPage) && <DropdownMenuSeparator className="my-0" />}
                <div
                  className="max-h-60 overflow-y-auto"
                  onScroll={handleFolderListScroll}
                >
                  {isFoldersLoading ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground">
                      Loading...
                    </div>
                  ) : (
                    <>
                      {availableFolders.map((folder) => (
                        <DropdownMenuIconItem
                          key={folder.uuid}
                          onClick={(event) => {
                            event.stopPropagation();
                            onMoveToFolder(folder.uuid);
                          }}
                          icon={<FolderInput className="h-4 w-4" />}
                        >
                          <span>{folder.name}</span>
                        </DropdownMenuIconItem>
                      ))}
                      {!currentFolderId && availableFolders.length === 0 && (
                        <div className="px-3 py-3 text-sm text-muted-foreground">
                          {t("chat_folder_list.no_folders")}
                        </div>
                      )}
                      {isFetchingNextPage && (
                        <div className="px-3 py-3 text-sm text-muted-foreground">
                          Loading...
                        </div>
                      )}
                    </>
                  )}
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator className="my-0" />
            <DropdownMenuIconItem
              onClick={handleArchiveClick}
              data-testid="chat-action-archive"
              icon={<Archive className="h-4 w-4" />}
            >
              <span>{t("chat_list.archive")}</span>
            </DropdownMenuIconItem>
            <DropdownMenuSeparator className="my-0" />
            <DropdownMenuIconItem
              variant="destructive"
              onClick={handleDeleteClick}
              data-testid="chat-action-delete"
              icon={<Trash className="h-4 w-4 " />}
            >
              <span>{t("chat_list.delete")}</span>
            </DropdownMenuIconItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ChatActionMenu = memo(ChatActionMenuInner);
